/**
 * `useFetchActivity` — a COUNTER of dashboard fetches in flight (never
 * a boolean that a second concurrent fetch could stomp). Drives the
 * level-1 loading signal (`GlobalProgressBar`, plan §2.3): the bar is
 * `active` while `count > 0` and turns off only when the LAST fetch
 * settles.
 *
 * Two ways to feed it:
 *   - `track(promise)` — wrap a page-level fetch (KPIs / charts /
 *     texts). Increments on call, decrements exactly once on settle
 *     (success AND error — rejections propagate untouched).
 *   - `wrapDataSource(ds)` — wrap the injected `DashboardDataSource`
 *     so every table / drill / row-drill fetch the shared
 *     `DashboardBody` fires is counted too, without the body knowing.
 *
 * SWR wiring (PR-B): the portal page and the builder Preview each hold
 * one instance and mount a `GlobalProgressBar` on `active`.
 *
 * Generations: `newGeneration()` starts a new round of fetches (the
 * portal calls it when a new set of filters goes out). From then on
 * `active` counts only the fetches tracked after that call; the older
 * ones still settle (the page aborts them) but no longer keep the bar
 * lit for a round nobody will see. A host that never calls it counts
 * every fetch, as before.
 */
import { useCallback, useMemo, useRef, useState } from "react";

import type { DashboardDataSource } from "../dashboard-data";

export interface FetchActivity {
  /** True while at least one tracked fetch is in flight. */
  active: boolean;
  /** Count a promise: +1 now, -1 exactly once when it settles. */
  track: <T>(p: Promise<T>) => Promise<T>;
  /** Wrap a data source so all its fetch methods are tracked. */
  wrapDataSource: (ds: DashboardDataSource) => DashboardDataSource;
  /** Start a new round: earlier fetches stop counting (see above). */
  newGeneration: () => void;
}

export function useFetchActivity(): FetchActivity {
  // In-flight count of the CURRENT generation (what `active` reads).
  const [count, setCount] = useState(0);
  const generationRef = useRef(0);
  const inFlightRef = useRef(new Map<number, number>());
  const bump = useCallback((generation: number, delta: number) => {
    const next = (inFlightRef.current.get(generation) ?? 0) + delta;
    if (next === 0) inFlightRef.current.delete(generation);
    else inFlightRef.current.set(generation, next);
    if (generation === generationRef.current) setCount(next);
  }, []);
  // StrictMode-safe: the decrement lives in a `.finally` on the
  // promise, so a re-rendered component can't double-decrement.
  const track = useCallback(
    <T,>(p: Promise<T>): Promise<T> => {
      const generation = generationRef.current;
      bump(generation, 1);
      return p.finally(() => bump(generation, -1)) as Promise<T>;
    },
    [bump],
  );
  const newGeneration = useCallback(() => {
    generationRef.current += 1;
    setCount(inFlightRef.current.get(generationRef.current) ?? 0);
  }, []);

  const trackRef = useRef(track);
  trackRef.current = track;

  const wrapDataSource = useCallback(
    (ds: DashboardDataSource): DashboardDataSource => ({
      // Spread FIRST so anything this wrapper does not explicitly track
      // still reaches the body. Listing methods one by one made the
      // wrapper an allow-list, and an allow-list silently DELETES every
      // method added to the data source afterwards — `fetchAiReadout`
      // vanished exactly that way, and the readout block reported
      // "generated in the portal" while running IN the portal.
      // Pass-through is the safe default here; tracking is the opt-in.
      ...ds,
      // NOT tracked on purpose: `fetchAiReadout` is an LLM call that
      // takes ~10s. Counting it would keep the global hairline lit long
      // after every number on the page has landed — the bar would say
      // "still loading" about data that is already there. The readout
      // carries its own inline loading state instead.
      fetchTableData: (args) => trackRef.current(ds.fetchTableData(args)),
      fetchDrill: (args) => trackRef.current(ds.fetchDrill(args)),
      fetchRowDrill: (args) => trackRef.current(ds.fetchRowDrill(args)),
      ...(ds.fetchAllTableRows
        ? {
            fetchAllTableRows: (args) =>
              trackRef.current(ds.fetchAllTableRows!(args)),
          }
        : {}),
      // `exportTable` passes through `...ds` untracked on purpose: a
      // multi-minute server export must not keep the global loading bar
      // lit; the export control shows its own progress.
    }),
    [],
  );

  return useMemo(
    () => ({ active: count > 0, track, wrapDataSource, newGeneration }),
    [count, track, wrapDataSource, newGeneration],
  );
}
